/**
 * useChecklistPublicLink - Link externo da vistoria (lado interno)
 *
 * Consulta, gera (ou regera) e revoga o link externo único de uma vistoria.
 * Tudo via RPC autenticada; a validade vem do template e fica congelada na geração.
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-05 v1.2.0 - useChecklistAcceptanceLink: link exclusivo de aceite do cliente.
 *   RELEASE: 2026-09-28 v1.1.0 - Suporta identidade por documento ou e-mail e motivo de indisponibilidade.
 *   RELEASE: 2026-09-28 v1.0.1 - Corrigida chamada RPC destacada do cliente (TypeError em `this.rest`),
 *            que impedia consultar/gerar/revogar o link.
 *   RELEASE: 2026-09-27 v1.0.0 - Criação inicial.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

export interface ChecklistPublicLinkInfo {
  validity_days: number;
  has_owner_document: boolean;
  has_access_identity: boolean;
  identity_kind: 'document' | 'email';
  identity_owner?: 'user' | 'queue' | 'owner' | 'provider';
  unavailable_reason: string | null;
  link: { token: string; expires_at: string; created_at: string; validity_days: number; is_expired: boolean } | null;
}

/** URL pública da vistoria, montada com a origem atual do app (sem hardcode). */
export const buildPublicChecklistUrl = (token: string) => `${window.location.origin}/v/${token}`;

// Mantém o vínculo com o cliente: supabase.rpc usa `this.rest` internamente.
// Destacar o método (const rpc = supabase.rpc) deixava `this` indefinido e a chamada quebrava.
const rpc = (fn: string, args: Record<string, unknown>) =>
  (supabase.rpc as unknown as (f: string, a: Record<string, unknown>) => Promise<{ data: any; error: any }>).call(supabase, fn, args);

export function useChecklistPublicLink(instanceId: string | null, enabled = true) {
  const qc = useQueryClient();
  const key = ['checklist-public-link', instanceId];

  const query = useQuery({
    queryKey: key,
    enabled: !!instanceId && enabled,
    queryFn: async () => {
      const { data, error } = await rpc('get_checklist_public_link', { p_instance_id: instanceId, p_tenant_hint: null });
      if (error) throw error;
      return data as ChecklistPublicLinkInfo;
    },
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: key });
    qc.invalidateQueries({ queryKey: ['inspection-timeline'] });
  };

  const generate = useMutation({
    mutationFn: async () => {
      const { data, error } = await rpc('generate_checklist_public_link', { p_instance_id: instanceId, p_tenant_hint: null });
      if (error) throw error;
      return data as { token: string; expires_at: string };
    },
    onSuccess: () => { refresh(); toast.success('Link externo gerado'); },
    onError: (e: Error) => toast.error(e.message || 'Não foi possível gerar o link'),
  });

  const revoke = useMutation({
    mutationFn: async () => {
      const { error } = await rpc('revoke_checklist_public_link', { p_instance_id: instanceId, p_tenant_hint: null });
      if (error) throw error;
    },
    onSuccess: () => { refresh(); toast.success('Link externo revogado'); },
    onError: (e: Error) => toast.error(e.message || 'Não foi possível revogar o link'),
  });

  return { ...query, generate, revoke };
}

/** v1.2.0: situação do link exclusivo de aceite do cliente (sem expor documento). */
export interface ChecklistAcceptanceLinkInfo {
  has_owner_document: boolean;
  owner_pending: boolean;
  unavailable_reason: string | null;
  link: { token: string; expires_at: string; created_at: string; is_expired: boolean; opened: boolean } | null;
}

/**
 * v1.2.0: link de aceite do cliente, separado do link de execução.
 * O cliente confirma o CPF/CNPJ do proprietário congelado na vistoria e só pode dar o aceite.
 */
export function useChecklistAcceptanceLink(instanceId: string | null, enabled = true) {
  const qc = useQueryClient();
  const key = ['checklist-acceptance-link', instanceId];

  const query = useQuery({
    queryKey: key,
    enabled: !!instanceId && enabled,
    queryFn: async () => {
      const { data, error } = await rpc('get_checklist_acceptance_link', { p_instance_id: instanceId, p_tenant_hint: null });
      if (error) throw error;
      return data as ChecklistAcceptanceLinkInfo;
    },
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: key });
    qc.invalidateQueries({ queryKey: ['inspection-timeline'] });
  };

  const generate = useMutation({
    mutationFn: async () => {
      const { data, error } = await rpc('generate_checklist_acceptance_link', { p_instance_id: instanceId, p_tenant_hint: null });
      if (error) throw error;
      return data as { token: string; expires_at: string };
    },
    onSuccess: () => { refresh(); toast.success('Link de aceite gerado'); },
    onError: (e: Error) => toast.error(e.message || 'Não foi possível gerar o link de aceite'),
  });

  const revoke = useMutation({
    mutationFn: async () => {
      const { error } = await rpc('revoke_checklist_acceptance_link', { p_instance_id: instanceId, p_tenant_hint: null });
      if (error) throw error;
    },
    onSuccess: () => { refresh(); toast.success('Link de aceite revogado'); },
    onError: (e: Error) => toast.error(e.message || 'Não foi possível revogar o link de aceite'),
  });

  return { ...query, generate, revoke };
}
